import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import ArticleCard from '@/components/base/ArticleCard';
import { categories, articles } from '@/mocks/articles';

export default function CategoryPage() {
  const { t } = useTranslation();
  const { slug } = useParams<{ slug: string }>();

  const currentCategory = categories.find((c) => c.slug === slug) || categories[0];
  const categoryArticles = articles.filter((a) => a.categorySlug === slug);

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${currentCategory.name} - ${t('siteName')}`,
    description: currentCategory.description,
    url: `${window.location.origin}/category/${slug}`,
    hasPart: categoryArticles.map((article) => ({
      '@type': 'Article',
      headline: article.title,
      url: `${window.location.origin}/article/${article.slug}`,
      description: article.excerpt,
    })),
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      <ScrollToTop />
      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-16">
          {/* Category Header */}
          <section className="bg-background-100 border-b border-background-200">
            <div className="max-w-7xl mx-auto px-4 md:px-6 py-10 md:py-14">
              <Breadcrumb items={[{ label: currentCategory.name }]} />
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mt-2">
                <div>
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-500 text-background-50">
                      <i className={`${currentCategory.icon} text-lg`}></i>
                    </div>
                    <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading">
                      {currentCategory.name}
                    </h1>
                  </div>
                  <p className="text-foreground-500 max-w-xl">
                    {currentCategory.description}
                  </p>
                </div>
                <span className="text-sm text-foreground-400 bg-background-50 px-4 py-2 rounded-full border border-background-200 whitespace-nowrap">
                  共 {currentCategory.articleCount} 篇文章
                </span>
              </div>
            </div>
          </section>

          {/* Category Tabs */}
          <section className="border-b border-background-200 bg-background-50 sticky top-16 z-30">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <div className="flex items-center gap-1 overflow-x-auto py-3 scrollbar-hide">
                {categories.map((cat) => (
                  <Link
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-200 ${
                      cat.slug === slug
                        ? 'bg-primary-500 text-background-50'
                        : 'text-foreground-600 hover:text-foreground-900 hover:bg-background-100'
                    }`}
                  >
                    <i className={`${cat.icon} mr-1.5`}></i>
                    {cat.name}
                  </Link>
                ))}
              </div>
            </div>
          </section>

          {/* Articles Grid */}
          <section className="py-10 md:py-14">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              {categoryArticles.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {categoryArticles.map((article) => (
                    <ArticleCard key={article.slug} article={article} />
                  ))}
                </div>
              ) : (
                <div className="text-center py-20">
                  <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-200 text-foreground-400">
                    <i className="ri-inbox-line text-2xl"></i>
                  </div>
                  <p className="text-foreground-500">{t('category.noArticles')}</p>
                </div>
              )}
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}